import { yupResolver } from '@hookform/resolvers/yup';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { CardHeader, Grid, IconButton } from '@mui/material';
import moment, { Moment } from 'moment';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { PeriodFilterValuesEnum } from 'utils/periodEnum';
import { useStoreSelector } from 'v3/data/store';
import { clearFilters, setContractingList, setFilters, setIsCustomDate } from 'v3/data/store/contracting/actions';
import { ContractingStateEnum } from 'v3/data/store/contracting/enums';
import { filtersInitialState } from 'v3/data/store/contracting/state';
import { FiltersType } from 'v3/data/store/contracting/types';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { getStorePermissions, restoreStoreList } from 'v3/data/store/stores/actions';
import { IStoreItem } from 'v3/data/store/stores/types';
import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import * as api from 'v3/services/api';
import { MiniButton } from 'v3/shared/components/Button/MiniButton';
import { ContractingStatusMap } from 'v3/shared/components/Filters/MainFilters/enum';
import { filterRequestFactory, getDateRange, toDateOnly } from 'v3/shared/components/Filters/MainFilters/factory';
import { DatePickerFormController } from 'v3/shared/components/Inputs/DatePickerFormController';
import { DropdownSelectFormController } from 'v3/shared/components/Inputs/DropdownSelectFormController';
import { InputFormController } from 'v3/shared/components/Inputs/InputFormController';
import { contractingFilterSchema } from 'v3/shared/schemas/searchString.schema';
import { BodyLarge } from 'v3/shared/typography/BodyLarge';
import { FlexButton } from '../../Button/FlexButton';
import { TextButton } from '../../Button/TextButton';
import { StoreFilter } from '../StoreFilter';
import { CustomFilterType } from 'v3/shared/components/Filters/MainFilters/types';
import { AlignButtons, CardContentMain, CardContentStyled, CardStyled, DownIcon, GridSectionStyled, GridStyled, UpIcon } from './styles';
import { TextMaskCustom } from '../../Inputs/MaskCustomInput';

const statusOptions = Object.values(ContractingStateEnum).map((status) => ({
  value: status,
  label: ContractingStatusMap[status],
}));

export const MainFilters: React.FC = () => {
  const { shouldFilterByStore, isMaster, isStore, isGroupAdmin } = getStorePermissions();
  const [expanded, setExpanded] = useState(isMaster);
  const [openModal, setOpenModal] = useState(false);
  const { isTablet } = useBreakpoints();
  const { filter } = useStoreSelector((store) => store.contracting);
  const { ...customFilters } = filter;
  const { storeList } = useStoreSelector((store) => store.store);
  const { id, group } = useStoreSelector((store) => store.store.storeLogged);

  const {
    control,
    getValues,
    reset,
    setValue,
    trigger,
    formState: { isValid },
  } = useForm<CustomFilterType>({
    mode: 'onChange',
    values: customFilters,
    reValidateMode: 'onChange',
    resolver: yupResolver(contractingFilterSchema) as any,
  });
  useEffect(() => {
    trigger();
  }, []);
  const setInitialFilterAccordingMasterAdminRole = useCallback(
    (isResetFilters?: boolean) => {
      const { startDate, finalDate } = getDateRange('month');
      const allStoreIds = storeList.map((store) => store.id);

      const initialValuesMasterAdmin: FiltersType = {
        ...filtersInitialState,
        storeIds: allStoreIds,
        period: 'month',
        startDate,
        finalDate,
      };
      const initialValuesGroupAdmin: FiltersType = {
        ...filtersInitialState,
        storeIds: allStoreIds,
      };

      let filters: FiltersType = filter;

      const initialFilters = isMaster
        ? initialValuesMasterAdmin
        : isGroupAdmin
          ? initialValuesGroupAdmin
          : filtersInitialState;

      if (isResetFilters) {
        filters = initialFilters;
        reset(filters);
        const isPaginationChanged =
          filter.rowsPerPage !== filtersInitialState.rowsPerPage || filter.page !== filtersInitialState.page;

        if (isStore && !isPaginationChanged) {
          getContractingList(filters);
        }
      } else {
        const customFiltersHandled = {
          ...filter,
          finalDate: filter.finalDate ?? initialFilters.finalDate,
          startDate: filter.startDate ?? initialFilters.startDate,
          period: filter.isCustomDate ? '' : filters.period.isNotEmpty ? filters.period : initialFilters.period,
        };

        filters = customFiltersHandled;
        getContractingList(filters);
      }

      setFilters(filters);
    },
    [filter.storeIds, filter.rowsPerPage, filter.page, id, group],
  );

  useEffect(() => {
    const hasStoreIds = (isMaster || isGroupAdmin) && filter.storeIds.isNotEmpty;

    if (id.isNotEmpty && group.isNotEmpty && (hasStoreIds || isStore)) setInitialFilterAccordingMasterAdminRole();
  }, [setInitialFilterAccordingMasterAdminRole]);

  const filterOnlyCheckedStores = (storeItemsList: IStoreItem[]) => {
    const desiredStoreList = storeItemsList.filter((store) => store.checked === true);
    return desiredStoreList.map((store) => store.id);
  };

  const getContractingList = useCallback(
    async (filters: FiltersType) => {
      const filterValues = getValues();
      const desiredStoreIds = filterOnlyCheckedStores(storeList);
      const filtersFormated = filterRequestFactory(filterValues, filters);

      try {
        const response = await api.getContractingList({
          ...filtersFormated,
          storeIds: [...desiredStoreIds],
          storeId: id,
        });
        setContractingList(response.data);
      } catch (error) {
        showErrorDialog('Erro ao obter lista de contratações', error);
      }
    },
    [filter],
  );

  const handlePeriodChange = (value: keyof typeof PeriodFilterValuesEnum | '') => {
    const { startDate, finalDate } = getDateRange(value);
    setFilters({ ...filter, startDate, finalDate, period: value });
    setIsCustomDate(false);
  };

  const handleClear = async () => {
    clearFilters({ keepStoreIds: true });
    setInitialFilterAccordingMasterAdminRole(true);
    setExpanded(false);
    restoreStoreList(storeList);
  };

  const startDateWatch = useWatch({ control, name: 'startDate' });
  const finalDateWatch = useWatch({ control, name: 'finalDate' });

  const disableOutsideLastSixMonths = useCallback(
    (date: unknown): boolean => {
      const isAdmin = isMaster;

      const dateMoment = moment(date as Moment);
      if (!dateMoment.isValid()) return true;

      const end = moment().endOf('day');
      const start = moment().subtract(6, 'months');
      return isAdmin ? !dateMoment.isBetween(start, end, 'day', '[]') : false;
    },
    [group],
  );

  const handleSearchButtonClick = () => {
    const paginationResetFilter = {
      ...filter,
      page: filtersInitialState.page,
      rowsPerPage: filtersInitialState.rowsPerPage,
    };
    setFilters(paginationResetFilter);
    const isPaginationChanged =
      filter.rowsPerPage !== filtersInitialState.rowsPerPage || filter.page !== filtersInitialState.page;
    if (isPaginationChanged) return;
    getContractingList(paginationResetFilter);
  };

  return (
    <CardStyled>
      <CardHeader title={<BodyLarge>Filtro</BodyLarge>} />
      <CardContentStyled>
        <GridSectionStyled container spacing={2}>
          <Grid size={{ xs: 12, sm: 2, md: 2, lg: 4 }}>
            <InputFormController
              data-testid="input-cpf-or-name"
              inputType="tel"
              formControl={control}
              formControlName="searchString"
              label="Procurar por CPF"
              placeholder="Procurar por CPF"
              mask="000.000.000-00"
              customMask={TextMaskCustom}
              fullWidth
              startAdornmentIcon={SearchIcon}
              onChangeInput={(value) => {
                const period = getValues('period') ?? '';
                setFilters({ ...filter, searchString: value, period });
              }}
            />
          </Grid>

          <GridStyled size={{ xs: 12, sm: 2, md: 2, lg: 2 }}>
            <DropdownSelectFormController
              data-testid="select-form-controller-status"
              formControl={control}
              fullWidth
              formControlName="status"
              label="Status"
              selectValues={statusOptions}
              onChangeSelect={(status) => setFilters({ ...filter, status })}
            />
          </GridStyled>

          <Grid size={{ xs: 12, sm: 8, md: 8, lg: 6 }} order={{ xs: 99, sm: 'unset' }}>
            <AlignButtons>
              <IconButton onClick={() => setExpanded(!expanded)}>{expanded ? <UpIcon /> : <DownIcon />}</IconButton>
              {isTablet ? (
                <MiniButton icon={SearchIcon} handleClick={() => handleSearchButtonClick()} disabled={!isValid} />
              ) : (
                <FlexButton
                  text="Pesquisar"
                  size="small"
                  handleClick={() => handleSearchButtonClick()}
                  disabled={!isValid}
                />
              )}
              {shouldFilterByStore &&
                (isTablet ? (
                  <MiniButton
                    icon={StorefrontIcon}
                    handleClick={() => setOpenModal(true)}
                    $backgroundColor="transparent"
                    $outline
                  />
                ) : (
                  <TextButton
                    startIcon={StorefrontIcon}
                    text="Filtrar lojas"
                    $outline
                    handleClick={() => setOpenModal(true)}
                    size="small"
                  />
                ))}
              <MiniButton text="Limpar" $backgroundColor="transparent" handleClick={handleClear} />
            </AlignButtons>
          </Grid>

          <Grid size={{ xs: 12 }}>
            {expanded && (
              <CardContentMain>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4, md: 4, lg: 3 }}>
                    <DropdownSelectFormController
                      data-testid="select-form-controller-period"
                      formControl={control}
                      formControlName="period"
                      label="Período"
                      selectValues={PeriodFilterValuesEnum}
                      enumType
                      shouldRenderVoid={!isMaster}
                      defaultValue={customFilters.period}
                      onChangeSelect={handlePeriodChange}
                    />
                  </Grid>
                  <Fragment>
                    <Grid size={{ xs: 12, sm: 4, md: 4, lg: 3 }}>
                      <DatePickerFormController
                        formControl={control}
                        formControlName="startDate"
                        label="Início"
                        defaultValue={customFilters.startDate ? moment(customFilters.startDate) : undefined}
                        onChangeDatePicker={(startDate) => {
                          setFilters({ ...filter, startDate: toDateOnly(startDate), period: '' });
                          setValue('period', '');
                          setIsCustomDate(true);
                        }}
                        shouldDisableDate={disableOutsideLastSixMonths}
                        disableFuture
                        maxDate={finalDateWatch ? moment(finalDateWatch) : undefined}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4, md: 4, lg: 3 }}>
                      <DatePickerFormController
                        formControl={control}
                        formControlName="finalDate"
                        label="Fim"
                        defaultValue={customFilters.finalDate ? moment(customFilters.finalDate) : undefined}
                        onChangeDatePicker={(finalDate) => {
                          setFilters({ ...filter, finalDate: toDateOnly(finalDate), period: '' });
                          setValue('period', '');
                          setIsCustomDate(true);
                        }}
                        shouldDisableDate={disableOutsideLastSixMonths}
                        disableFuture
                        minDate={startDateWatch ? moment(startDateWatch) : undefined}
                      />
                    </Grid>
                  </Fragment>
                </Grid>
              </CardContentMain>
            )}
          </Grid>
        </GridSectionStyled>

        <StoreFilter open={openModal} setOpenModal={setOpenModal} />
      </CardContentStyled>
    </CardStyled>
  );
};
